'use client';

import { useState } from 'react';
import clsx from 'clsx';
import type { CallbackSettingsInput, FormLocale } from '@jlg-website/shared';
import { isOutsideBusinessHours } from './shared/business-hours-gate';
import type { CallbackAnswer, CompletionAction, ConfirmCallback } from './shared/type';
import { useVisitorTimeZone } from './shared/use-visitor-time-zone';
import { CallbackQuestion } from './question';
import { ScheduleCallback } from './picker';
import { CallNow } from './call-now';

type CallbackSchedulerProps = {
  settings: CallbackSettingsInput;
  phone: string;
  zipCode: string;
  takenAt?: string[];
  locale?: FormLocale;
  /** Books the slot the visitor picked in the calendar. */
  onConfirm?: ConfirmCallback;
  /** Books a call back asked for right away — a separate path, handled differently downstream. */
  onCallNow?: ConfirmCallback;
  /** The form's closing button, handed down so only the success panel can offer it. */
  completionAction?: CompletionAction;
  className?: string;
};

/**
 * How a qualified lead arranges their call back. One question opens it — be called now, or pick a
 * time — and the answer decides what follows: "no" reveals the calendar under the question it was
 * answered on, "yes" leaves for a confirmation of its own.
 *
 * The office day no longer decides which of the two is offered, only how the question is put; a
 * visitor who arrives at 3am is asked for permission rather than offered a convenience.
 *
 * The picker is mounted from the start and only hidden, never dropped: backing out of the
 * immediate confirmation returns to a question whose calendar must still hold the day already
 * picked on it.
 */
export function CallbackScheduler({
  settings,
  phone,
  zipCode,
  takenAt,
  locale = 'en',
  onConfirm,
  onCallNow,
  completionAction,
  className,
}: CallbackSchedulerProps) {
  const [answer, setAnswer] = useState<CallbackAnswer | null>(null);
  const callingNow = answer === 'now';

  const timeZone = useVisitorTimeZone(zipCode, settings);

  // Judged once, on arrival: the wording must not change under a visitor who is already reading
  // the question because the office happened to open while they sat there.
  const [outsideBusinessHours] = useState(() =>
    isOutsideBusinessHours({ settings, visitorTimeZone: settings.tz, now: new Date() })
  );

  return (
    <div className={clsx('flex w-full flex-col gap-5', className)}>
      <div hidden={callingNow}>
        <ScheduleCallback
          settings={settings}
          phone={phone}
          zipCode={zipCode}
          takenAt={takenAt}
          locale={locale}
          onConfirm={onConfirm}
          prompt={
            <CallbackQuestion
              outsideBusinessHours={outsideBusinessHours}
              answer={answer}
              locale={locale}
              onAnswer={setAnswer}
            />
          }
          promptAnswered={answer === 'schedule'}
          completionAction={completionAction}
        />
      </div>

      {callingNow && (
        <CallNow
          phone={phone}
          officeTimeZone={settings.tz}
          leadTimeZone={timeZone}
          locale={locale}
          onConfirm={onCallNow}
          onBack={() => setAnswer(null)}
          completionAction={completionAction}
        />
      )}
    </div>
  );
}

export default CallbackScheduler;
